import React, { useState, useEffect } from 'react'; // useEffectを追加
import { Container, Card, Button, Row, Col, Alert, Form, Badge, Spinner } from 'react-bootstrap'; // Badge, Spinnerを追加
import { Link } from 'react-router-dom';
import { db, auth } from '../firebase';
import { doc, setDoc, deleteDoc, serverTimestamp, onSnapshot } from 'firebase/firestore'; // onSnapshotを追加

function LoginManagement() {
  const [maintenanceType, setMaintenanceType] = useState('通常メンテナンス');
  const [maintenanceContent, setMaintenanceContent] = useState('');
  
  // ★追加：現在の状態を管理するState
  const [isMaintenance, setIsMaintenance] = useState(null); // null: 読込中, true: メンテ中, false: 通常時
  const [currentData, setCurrentData] = useState(null);

  // --- リアルタイム監視設定 ---
  useEffect(() => {
    const sessionDocRef = doc(db, "system_settings", "session_control");
    
    // ドキュメントの有無を監視
    const unsubscribe = onSnapshot(sessionDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setIsMaintenance(true);
        setCurrentData(data);
        // 現在設定されている内容をフォームに反映（任意）
        setMaintenanceType(data.type || '通常メンテナンス');
        setMaintenanceContent(data.content || '');
      } else {
        setIsMaintenance(false);
        setCurrentData(null);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleForceLogout = async () => {
    const currentAdminUid = auth.currentUser ? auth.currentUser.uid : 'unknown';
    const confirmMsg = 
      `【${maintenanceType}】を実行しますか？\n` +
      `管理者以外の全ユーザーを強制的にログアウトさせます。`;

    if (window.confirm(confirmMsg)) {
      try {
        await setDoc(doc(db, "system_settings", "session_control"), {
          forceLogoutAt: serverTimestamp(),
          status: "restricted",
          type: maintenanceType,
          content: maintenanceContent,
          createdAt: serverTimestamp(),
          startedBy: currentAdminUid
        });
        alert("メンテナンスを開始しました。");
      } catch (error) {
        console.error(error);
        alert("エラーが発生しました。");
      }
    }
  };

  const handleEnableLogin = async () => {
    if (window.confirm("ログイン制限を解除しますか？")) {
      try {
        await deleteDoc(doc(db, "system_settings", "session_control"));
        alert("制限を解除しました。");
        setMaintenanceContent('');
      } catch (error) {
        console.error(error);
        alert("解除に失敗しました。");
      }
    }
  };

  return (
    <main>
      <Container className="my-4">
        <Card className="shadow-sm">
          <Card.Header className="p-3 d-flex justify-content-between align-items-center bg-white">
            <h2 className="h4 mb-0">ログイン管理</h2>
            {/* ★★★ 現在のステータス表示 ★★★ */}
            <div>
              {isMaintenance === null ? (
                <Spinner animation="border" size="sm" variant="secondary" />
              ) : isMaintenance ? (
                <Badge bg="danger" className="p-2 px-3">メンテナンス中</Badge>
              ) : (
                <Badge bg="success" className="p-2 px-3">稼働中（通常）</Badge>
              )}
            </div>
          </Card.Header>

          <Card.Body className="p-4">
            {/* ★★★ メンテナンス中の場合のアラート表示 ★★★ */}
            {isMaintenance && currentData && (
              <Alert variant="warning" className="mb-4">
                <h5 className="alert-heading fw-bold">現在メンテナンスモードが有効です</h5>
                <p className="mb-0 small">
                  種別：{currentData.type} <br />
                  内容：{currentData.content || '（未設定）'}
                </p>
              </Alert>
            )}

            <Alert variant="info" className="small">
              解除ボタンを押すまで、一般ユーザー（社員・顧客）はログイン画面で足止めされ、ログイン中のユーザーは順次強制ログアウトされます。
            </Alert>

            <div className="bg-light p-3 rounded mb-4 border">
              <h4 className="h6 fw-bold mb-3">メンテナンス設定の編集</h4>
              
              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold">メンテナンス種別</Form.Label>
                <div>
                  <Form.Check
                    inline
                    label="通常メンテナンス"
                    type="radio"
                    name="maintenanceType"
                    checked={maintenanceType === '通常メンテナンス'}
                    onChange={() => setMaintenanceType('通常メンテナンス')}
                    disabled={isMaintenance} // メンテ中は入力をロック（誤操作防止）
                  />
                  <Form.Check
                    inline
                    label="緊急メンテナンス"
                    type="radio"
                    name="maintenanceType"
                    checked={maintenanceType === '緊急メンテナンス'}
                    onChange={() => setMaintenanceType('緊急メンテナンス')}
                    disabled={isMaintenance}
                  />
                </div>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold">メンテナンス内容（ログイン画面に表示）</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="内容を入力してください"
                  value={maintenanceContent}
                  onChange={(e) => setMaintenanceContent(e.target.value)}
                  disabled={isMaintenance}
                />
              </Form.Group>
            </div>

            <Row className="g-3 mt-2">
              <Col xs={12} md={6}>
                <Button 
                  variant="danger" 
                  size="lg" 
                  onClick={handleForceLogout} 
                  className="w-100 shadow-sm"
                  disabled={isMaintenance} // メンテ中なら開始ボタンを押せなくする
                >
                  メンテナンス開始
                </Button>
              </Col>
              <Col xs={12} md={6}>
                <Button 
                  variant="success" 
                  size="lg" 
                  onClick={handleEnableLogin} 
                  className="w-100 shadow-sm"
                  disabled={!isMaintenance} // 通常時なら解除ボタンを押せなくする
                >
                  メンテナンス解除
                </Button>
              </Col>
            </Row>
            
            <hr className="my-4" />
            <Link to="/admin">管理者メニューに戻る</Link>
          </Card.Body>
        </Card>
      </Container>
    </main>
  );
}

export default LoginManagement;